import { AzureKeyCredential, SearchClient } from '@azure/search-documents';
import Vue from '../../plugins/vueFactory';

const state = {
  facets: {},
  isPending: true,
  paginationSettings: {
    defaultTop: 12,
    topOptions: [12, 24, 48, 96]
  },
  path: undefined,
  preFilter: {},
  products: [],
  propertyFacetsField: 'propertyFacets',
  propertiesMetadata: [],
  query: {
    page: 1,
    top: 12
  },
  settings: {
    apiVersion: undefined,
    indexName: undefined,
    searchServiceUrl: undefined,
    searchServiceQueryApiKey: undefined
  },
  toolAdvisorBaseUrl: undefined,
  imageType: 'teaser',
  totalCount: 0
};

const actions = {
  async getProducts({ commit, getters, rootState, state }, payload) {
    commit('setIsPending', true);

    const searchClient = new SearchClient(
      state.settings.searchServiceUrl,
      state.settings.indexName,
      new AzureKeyCredential(state.settings.searchServiceQueryApiKey)
    );

    const cultureFilter = `culture eq '${rootState.culture}'`;
    const preFilter = cultureFilter + getters.odata({ query: state.preFilter });

    if (payload?.initialize) {
      const initResult = await searchClient.search('', {
        facets: [`${state.propertyFacetsField},count:1000`],
        filter: `${preFilter}`,
        top: 0
      });

      commit('setFacets', {
        newFacets: initResult.facets
      });
    }

    const documentsResult = await searchClient.search('', {
      facets: [`${state.propertyFacetsField},count:1000`],
      filter: `${preFilter}${getters.odata({ query: state.query })}`,
      includeTotalCount: true,
      orderBy: ['position asc'],
      skip: (state.query.page - 1) * state.query.top,
      top: state.query.top
    });

    const searchIterator = documentsResult.results;
    const page = await searchIterator.byPage().next();
    commit('setFacets', {
      newFacets: page.value.facets
    });
    commit('setProducts', page.value.results);
    commit('setTotalCount', page.value.count);

    commit('setIsPending', false);

    for (const key of getters.activeFacets) {
      const overrideFacetsResult = await searchClient.search('', {
        facets: [`${state.propertyFacetsField},count:1000`],
        filter: `${preFilter}${getters.odata({
          query: state.query,
          except: key
        })}`,
        top: 0
      });

      const overrideFacets = {};
      overrideFacets[state.propertyFacetsField] = overrideFacetsResult.facets[
        state.propertyFacetsField
      ].filter(x => x.value.startsWith(`${key}::`));
      commit('setFacets', {
        newFacets: overrideFacets,
        override: true
      });
    }
  },
  async getPropertiesMetadata({ commit, rootState }) {
    const result = await fetch(
      `/umbraco/api/products/GetPropertiesMetadata/${rootState.culture}`
    );
    const data = await result.json();
    commit('setPropertiesMetadata', data);
  }
};

const mutations = {
  setFacets(state, { newFacets, override }) {
    let propertyFacets = state.facets[state.propertyFacetsField];

    if (!propertyFacets) {
      propertyFacets = [];
    }

    const newPropertyFacets = newFacets[state.propertyFacetsField];
    for (const newFacet of newPropertyFacets) {
      const oldFacet = propertyFacets.find(x => x.value === newFacet.value);
      if (!oldFacet) {
        propertyFacets.push(newFacet);
      } else {
        oldFacet.count = newFacet.count;
        oldFacet.value = newFacet.value;
      }
    }

    if (!override) {
      const missingPropertyFacets = propertyFacets.filter(
        x => !newPropertyFacets.some(y => x.value === y.value)
      );
      for (const facet of missingPropertyFacets) {
        facet.count = 0;
      }
    }

    Vue.set(state.facets, state.propertyFacetsField, propertyFacets);
  },
  setImageType(state, payload) {
    state.imageType = payload;
  },
  setIsPending(state, payload) {
    state.isPending = payload;
  },
  setPath(state, payload) {
    state.path = payload;
  },
  setPreFilter(state, payload) {
    const filters = payload.split('&');
    for (const filter of filters) {
      const parts = filter.split('=');
      const key = decodeURI(parts[0]);
      const value = decodeURI(parts[1]);
      if (!state.preFilter[key]) state.preFilter[key] = [];
      state.preFilter[key].push(value);
    }
  },
  setProducts(state, payload) {
    state.products = payload;
  },
  setPropertiesMetadata(state, payload) {
    state.propertiesMetadata = payload;
  },
  setQuery(state, payload) {
    const newQuery = {};

    for (const encodedKey in payload) {
      if (encodedKey.toLowerCase() === 'langchosen') continue;

      const encodedValue = payload[encodedKey];
      const key = decodeURI(encodedKey);
      if (Array.isArray(encodedValue)) {
        newQuery[key] = encodedValue.map(decodeURI);
      } else {
        newQuery[key] = [decodeURI(encodedValue)];
      }
    }
    newQuery.page = parseFloat(newQuery.page) || 1;
    newQuery.top =
      parseFloat(newQuery.top) || state.paginationSettings.defaultTop;

    // state.query must be set this way due to vue reactivity
    state.query = newQuery;
  },
  setSettings(state, payload) {
    state.settings.searchServiceUrl = payload.searchServiceUrl;
    state.settings.searchServiceQueryApiKey = payload.searchServiceQueryApiKey;
    state.settings.apiVersion = payload.apiVersion;
    state.settings.indexName = payload.indexName;
  },
  setToolAdvisorBaseUrl(state, payload) {
    state.toolAdvisorBaseUrl = payload;
  },
  setTotalCount(state, payload) {
    state.totalCount = payload;
  }
};

const getters = {
  activeFacets(state) {
    const facets = [];
    for (const facet of state.facets[state.propertyFacetsField]) {
      const parts = facet.value.split('::');
      const key = parts[0];
      if (!facets.includes(key) && (state.preFilter[key] || state.query[key])) {
        facets.push(key);
      }
    }
    return facets;
  },
  buildQuery: (state, getters) => newQuery => {
    const query = { ...state.query };

    for (const key in newQuery) {
      const value = newQuery[key];

      if (key === 'page' || key === 'top') {
        query[key] = value;
        continue;
      }

      if (getters.isFilterActive(key, value)) {
        query[key] = query[key].filter(x => x !== value);
      } else {
        if (Array.isArray(query[key])) {
          query[key] = [...query[key], value];
        } else {
          query[key] = [value];
        }
      }
    }

    if (query.page === 1) delete query.page;
    if (query.top === state.paginationSettings.defaultTop) delete query.top;

    return {
      path: state.path,
      query
    };
  },
  buildResetQuery(state) {
    const query = {};

    if (state.query.top !== state.paginationSettings.defaultTop) {
      query.top = state.query.top;
    }

    return {
      path: state.path,
      query
    };
  },
  isAnyFilterActive(state) {
    for (const key in state.query) {
      if (key === 'page' || key === 'top') {
        continue;
      }
      if (state.query[key].length) {
        return true;
      }
    }
    return false;
  },
  isFilterActive: state => (key, value) => state.query[key]?.includes(value),
  mapFacets(state) {
    const facets = {};
    const originalFacets = { ...state.facets };
    const propertyFacets = state.facets[state.propertyFacetsField];
    if (propertyFacets) {
      for (const propertyFacet of propertyFacets) {
        const parts = propertyFacet.value.split('::');
        const key = parts[0];
        const value = parts[1];
        const propertyId = parseInt(parts[2]);
        const propertyValueId = parseInt(parts[3]);

        const facetMetadata = state.propertiesMetadata.find(
          x => x.propertyId === propertyId
        ) ?? {
          defaultMaxFilterCount: 99,
          filterPosition: 99
        };

        if (facets[key] === undefined) {
          facets[key] = {
            values: [],
            metadata: facetMetadata,
            propertyId
          };
        }

        const facetValueMetadata = facetMetadata.propertyValues
          ? facetMetadata.propertyValues.find(
              x => x.propertyValueId === propertyValueId
            ) ?? {}
          : {};

        const facetValue = {
          count: propertyFacet.count,
          value,
          metadata: facetValueMetadata,
          propertyValueId
        };
        facets[key].values.push(facetValue);
      }
      delete originalFacets[state.propertyFacetsField];
    }
    return { ...facets, ...originalFacets };
  },
  odata: state => ({ query, except }) => {
    let odata = '';
    for (const key in query) {
      if (key === except) continue;
      const values = query[key];
      if (Array.isArray(values) && values.length) {
        let isFirstIteration = true;
        for (const value of values) {
          if (isFirstIteration) {
            odata += ' and (';
            isFirstIteration = false;
          } else {
            odata += ' or ';
          }
          odata += `properties/any(x: x/propertyName eq '${key}' and x/propertyValues/any(y: y/propertyValue eq '${value}') )`;
        }
        odata += ')';
      }
    }
    return odata;
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
